import _ from 'underscore';
import { useMemo } from 'react';
import { useDataSource } from '@bingads-webui-react/data-source';
import { useDataSelection } from '@bingads-webui-react/data-selection';
import { buildMap, filterTreeItems } from '@bingads-webui-fluent-component/selection-tree';

import { useAggregatedData } from '../context';
import { AggregationMode } from '../types';

const valueSubtreeHasCheckedItems = (value, flatTreeItems, itemsMap, dataSelection) => {
  // If item is checked we can return true directly
  if (_.some(dataSelection, (selection) => selection.value === value)) {
    return true;
  }

  const entry = itemsMap.get(value);

  // If item isn't checked and has no children, we can return false directly
  if (_.isEmpty(entry.children)) {
    return false;
  }

  // Otherwise, we need to check if any of the children are checked
  return _.any(entry.children, (child) => {
    const childValue = flatTreeItems[child]?.value;
    return valueSubtreeHasCheckedItems(childValue, flatTreeItems, itemsMap, dataSelection);
  });
};

const isCurrentCollectionList = (items) => items.every((item) => item.ancestor.length === 0 || item.children.length === 0);

interface UseTreeDataProps {
  mode: AggregationMode;
  showSelectedOnly?: boolean;
  customFilter?: (item: any, search: string) => boolean;
}
export const useTreeData = ({
  mode,
  showSelectedOnly,
  customFilter,
}: UseTreeDataProps) => {
  const { searchTerm } = useDataSource();
  const { dataSelection } = useDataSelection();
  const { getAggregatedData } = useAggregatedData();

  const items = useMemo(() => getAggregatedData(mode), [mode, getAggregatedData]);

  if (!items) {
    return {
      fullItems: [],
      items: [],
      itemsMap: new Map(),
      isList: false,
    };
  }

  const map = buildMap(items);
  let filteredItems = filterTreeItems(items, map, searchTerm, customFilter);

  if (showSelectedOnly) {
    filteredItems = _.filter(filteredItems, (item) => valueSubtreeHasCheckedItems(item.value, items, map, dataSelection));
  }

  const isList = isCurrentCollectionList(items);

  return {
    fullItems: items,
    items: filteredItems,
    itemsMap: map,
    isList,
  };
};
